/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/tbucket.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Creates groups of values - buckets - out of a `@timestamp` attribute.
 * The size of the buckets can be provided directly as a duration or period.
 * Alternatively, the bucket size can be chosen based on a recommended count
 * and a range.
 *
 * When using ES|QL in Kibana, the range can be derived automatically from the
 * `@timestamp` filter
 * that Kibana adds to the query.
 *
 * @example
 * FROM sample_data
 * | STATS count = COUNT(*) BY bucket = TBUCKET(3, "2023-10-23T12:00:00Z", "2023-10-23T14:00:00Z")
 * | SORT bucket
 *
 * @example
 * FROM sample_data
 * | STATS count = COUNT(*) BY bucket = TBUCKET(20, "2023-10-23T12:00:00Z", "2023-10-23T14:00:00Z")
 * | SORT bucket
 *
 * @example
 * FROM sample_data
 * | STATS min = MIN(@timestamp), max = MAX(@timestamp) BY bucket = TBUCKET(1 hour)
 * | SORT min
 *
 * @example
 * FROM sample_data
 * | STATS min = MIN(@timestamp), max = MAX(@timestamp) BY bucket = TBUCKET("1 hour")
 * | SORT min
 */
const definition: FunctionDefinition = {
  type: 'grouping',
  name: 'tbucket',
  signatures: [
    {
      params: [
        {
          name: 'buckets',
          type: 'date_period',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'date_period',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'text',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'buckets',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
